import type { AppliedChange } from "./types";

/**
 * What the assistant has set on the page, keyed by the index of the assistant message that set
 * it, so a message can be reverted on its own. Plain functions over the map: the hook owns the
 * instance, nothing here touches the DOM.
 */
export type ChangeLedger = Map<number, AppliedChange[]>;

/** Every recorded change in transcript order. */
export function allChanges(ledger: ChangeLedger): AppliedChange[] {
  return Array.from(ledger.keys())
    .sort((left, right) => left - right)
    .flatMap((messageIndex) => ledger.get(messageIndex) ?? []);
}

export function recordChanges(ledger: ChangeLedger, messageIndex: number, changes: AppliedChange[]): void {
  if (changes.length > 0) {
    ledger.set(messageIndex, [...(ledger.get(messageIndex) ?? []), ...changes]);
  }
}

export function changesOfMessage(ledger: ChangeLedger, messageIndex: number): AppliedChange[] {
  return ledger.get(messageIndex) ?? [];
}

/** The changes of this message and every later one, in transcript order. */
export function changesFrom(ledger: ChangeLedger, messageIndex: number): AppliedChange[] {
  return Array.from(ledger.keys())
    .filter((index) => index >= messageIndex)
    .sort((left, right) => left - right)
    .flatMap((index) => ledger.get(index) ?? []);
}

export function changesOfForm(ledger: ChangeLedger, formId: string): AppliedChange[] {
  return allChanges(ledger).filter((change) => change.formId === formId);
}

/** Forgets the given change records (by identity); a message left without changes is forgotten too. */
export function dropChanges(ledger: ChangeLedger, dropped: AppliedChange[]): void {
  Array.from(ledger.entries()).forEach(([messageIndex, changes]) => {
    const remaining = changes.filter((change) => !dropped.includes(change));
    if (remaining.length === 0) {
      ledger.delete(messageIndex);
    } else {
      ledger.set(messageIndex, remaining);
    }
  });
}

/**
 * How many distinct fields the assistant has changed on each form, in transcript order of first
 * change: a field set in two messages carries two records but is one changed field.
 */
export function countByForm(ledger: ChangeLedger): Map<string, number> {
  const pathsByForm = new Map<string, Set<string>>();
  allChanges(ledger).forEach((change) => {
    const paths = pathsByForm.get(change.formId) ?? new Set<string>();
    paths.add(change.path);
    pathsByForm.set(change.formId, paths);
  });
  return new Map(Array.from(pathsByForm.entries()).map(([formId, paths]) => [formId, paths.size]));
}
